import { useLazyQuery } from '@apollo/client';
import React, { useContext, useEffect } from 'react';
import { Route, Switch } from 'react-router-dom';
import AccessControl from '../../../../AccessControl';
import { MENU_ITEMS } from '../../../../app/components/sidebar/Sidebar';
import { AppContext } from '../../../../AppContext';
import {
  LIMIT,
  MODULES,
  ORDER_STATUS,
  ORDER_TYPE,
  PERMISSIONS_KEY,
  RETURN_ORDER_ITEM_STATUS,
  ROUTES,
  TABLE_FILTER_KEYS,
} from '../../../../common/constants';
import { getRedirectSubmenuItem } from '../../../../common/utils';
import Error404 from '../../../../Error404';
import useRetainedTableState from '../../../../hooks/useRetainedTableFilterState';
import RedirectControl from '../../../../RedirectControl';
import {
  NEW_ORDERS_COUNT_ADMIN_V2,
  NEW_RETURN_ORDER_COUNT_ADMIN,
  FULFILMENT_POSSIBILITY_ORDERS_COUNT_ADMIN,
} from './graphql/Queries';
import './new.less';
import NewAssignDelPartner from './pages/NewAssignDelPartner';
import NewConfirmOrder from './pages/NewConfirmOrder';
import NewFulfilmentPossibility from './pages/NewFulfilmentPossibility';
import NewDelivery from './pages/NewDelivery';
import NewReturn from './pages/NewReturn';

const tabs = MENU_ITEMS?.find(
  (menuItem) => menuItem?.key === MODULES?.ORDERS,
)?.subMenu?.find((menu) => menu?.key === MODULES?.NEW)?.tabKeys;

const NewOrdersWrapper = () => {
  const {
    dispatch,
    state: { permissions, orderCount },
  } = useContext(AppContext);

  const redirectArray = getRedirectSubmenuItem(permissions, tabs);
  const { retainedState } = useRetainedTableState();

  const [newOrdersAdminCountV2] = useLazyQuery(NEW_ORDERS_COUNT_ADMIN_V2, {
    fetchPolicy: 'network-only',
    onError() {},
  });

  const [newConfirmedOrdersAdminCountV2] = useLazyQuery(
    NEW_ORDERS_COUNT_ADMIN_V2,
    {
      fetchPolicy: 'network-only',
      onError() {},
    },
  );

  const [newReturnOrdersAdminCountV2] = useLazyQuery(
    NEW_RETURN_ORDER_COUNT_ADMIN,
    {
      onCompleted: (response) => {
        dispatch({
          type: 'SET_ORDER_COUNT',
          data: {
            ...orderCount,
            returnOrderCount: response?.returnOrdersAdmin?.count,
            returnRequestedOrderCount:
              response?.returnOrdersAdmin?.returnRequestedOrderCount,
          },
        });
      },
      fetchPolicy: 'network-only',
      onError() {},
    },
  );

  const [fulfilmentPossibilityOrdersAdminCount] = useLazyQuery(
    FULFILMENT_POSSIBILITY_ORDERS_COUNT_ADMIN,
    {
      fetchPolicy: 'network-only',
      onError() {},
    },
  );

  const getCountData = async () => {
    try {
      const retainedNewOrderFilters =
        retainedState?.[TABLE_FILTER_KEYS.NEW_ORDERS] || {};

      const retainedConfirmedFilters =
        retainedState?.[TABLE_FILTER_KEYS.NEW_RIDER_MANAGEMENT] || {};

      const retainedReturnFilters =
        retainedState?.[TABLE_FILTER_KEYS.NEW_RETURN] || {};

      const retainedFulfilmentFilters =
        retainedState?.[TABLE_FILTER_KEYS.NEW_FULFILMENT_POSSIBILITY] || {};

      const [
        placedResponse,
        confirmedResponse,
        returnResponse,
        fulfilmentResponse,
      ] = await Promise.all([
        newOrdersAdminCountV2({
          variables: {
            filter: {
              orderType: ORDER_TYPE?.NEW,
              showPlacedOrderCount: true,
              status:
                retainedNewOrderFilters?.filters?.status?.length > 0
                  ? retainedNewOrderFilters?.filters?.status
                  : [ORDER_STATUS?.PLACED?.value],
              skip:
                (retainedNewOrderFilters?.paginationProp?.current - 1) *
                (retainedNewOrderFilters?.paginationProp?.pageSize || 0),
              search: retainedNewOrderFilters?.searchTerm,
              limit: retainedNewOrderFilters?.paginationProp?.pageSize || LIMIT,
              orderIds: retainedNewOrderFilters?.filters?.orderNumber,
              deliveryType: retainedNewOrderFilters?.filters?.deliveryType,
              dateRange: {
                from: retainedNewOrderFilters?.filters?.createdAt?.from?.$d,
                to: retainedNewOrderFilters?.filters?.createdAt?.to?.$d,
              },
              deliveredOn: {
                from: retainedNewOrderFilters?.filters?.deliveredOn?.from?.$d,
                to: retainedNewOrderFilters?.filters?.deliveredOn?.to?.$d,
              },
              pincodeIds: retainedNewOrderFilters?.filters?.pincodes,
              statusTiming: retainedNewOrderFilters?.filters?.statusTiming,
              libraryIds: retainedNewOrderFilters?.filters?.name,
              city: retainedNewOrderFilters?.filters?.city,
            },
          },
        }),

        newConfirmedOrdersAdminCountV2({
          variables: {
            filter: {
              orderType: ORDER_TYPE?.NEW,
              showConfirmedOrderCount: true,

              status:
                retainedConfirmedFilters?.filters?.status?.length > 0
                  ? retainedConfirmedFilters?.filters?.status
                  : [
                      ORDER_STATUS?.READY_TO_SHIP?.value,
                      ORDER_STATUS?.CONFIRMED?.value,
                    ],
              skip:
                (retainedConfirmedFilters?.paginationProp?.current - 1) *
                (retainedConfirmedFilters?.paginationProp?.pageSize || 0),
              search: retainedConfirmedFilters?.searchTerm,
              limit:
                retainedConfirmedFilters?.paginationProp?.pageSize || LIMIT,
              orderIds: retainedConfirmedFilters?.filters?.orderNumber,
              deliveryType: retainedConfirmedFilters?.filters?.deliveryType,
              dateRange: {
                from: retainedConfirmedFilters?.filters?.createdAt?.from?.$d,
                to: retainedConfirmedFilters?.filters?.createdAt?.to?.$d,
              },
              deliveredOn: {
                from: retainedConfirmedFilters?.filters?.deliveredOn?.from?.$d,
                to: retainedConfirmedFilters?.filters?.deliveredOn?.to?.$d,
              },
              pincodeIds: retainedConfirmedFilters?.filters?.pincodes,
              statusTiming: retainedConfirmedFilters?.filters?.statusTiming,
              libraryIds: retainedConfirmedFilters?.filters?.name,
              city: retainedConfirmedFilters?.filters?.city,
            },
          },
        }),
        newReturnOrdersAdminCountV2({
          variables: {
            filter: {
              showReturnRequestedOrderCount: true,
              search: retainedReturnFilters?.searchTerm || '',
              limit: retainedReturnFilters?.paginationProp?.pageSize || LIMIT,
              skip:
                (retainedReturnFilters?.paginationProp?.current - 1) *
                (retainedReturnFilters?.paginationProp?.pageSize || 0),
              deliveryType: retainedReturnFilters?.filters?.deliveryType,
              dateRange: {
                from: retainedReturnFilters?.filters?.createdAt?.from?.$d,
                to: retainedReturnFilters?.filters?.createdAt?.to?.$d,
              },
              returnedOn: {
                from: retainedReturnFilters?.filters?.returnedOn?.from?.$d,
                to: retainedReturnFilters?.filters?.returnedOn?.to?.$d,
              },
              status:
                retainedReturnFilters?.filters?.status?.length > 0
                  ? retainedReturnFilters?.filters?.status
                  : [
                      RETURN_ORDER_ITEM_STATUS?.RETURN_REQUESTED?.value,
                      RETURN_ORDER_ITEM_STATUS?.READY_TO_PICKUP?.value,
                      RETURN_ORDER_ITEM_STATUS?.NOT_PICKED_UP?.value,
                    ],
              pincodeIds: retainedReturnFilters?.filters?.pincodes,
              deliveryPartnerIds:
                retainedReturnFilters?.filters?.deliveryPartnerName,
              returnOrderIds: retainedReturnFilters?.filters?.returnOrderNumber,
              toyIds: retainedReturnFilters?.filters?.productName,
              libraryIds: retainedReturnFilters?.filters?.name,
              receivingLibraryIds:
                retainedReturnFilters?.filters?.receivingLibName,
            },
          },
        }),
        fulfilmentPossibilityOrdersAdminCount({
          variables: {
            filter: {
              orderType: ORDER_TYPE?.NEW,
              status:
                retainedFulfilmentFilters?.filters?.status?.length > 0
                  ? retainedFulfilmentFilters?.filters?.status
                  : [ORDER_STATUS?.PLACED?.value],
              skip:
                (retainedFulfilmentFilters?.paginationProp?.current - 1) *
                (retainedFulfilmentFilters?.paginationProp?.pageSize || 0),
              search: retainedFulfilmentFilters?.searchTerm,
              limit: retainedFulfilmentFilters?.paginationProp?.pageSize || LIMIT,
              orderIds: retainedFulfilmentFilters?.filters?.orderNumber,
              deliveryType: retainedFulfilmentFilters?.filters?.deliveryType,
              dateRange: {
                from: retainedFulfilmentFilters?.filters?.createdAt?.from?.$d,
                to: retainedFulfilmentFilters?.filters?.createdAt?.to?.$d,
              },
              deliveredOn: {
                from: retainedFulfilmentFilters?.filters?.deliveredOn?.from?.$d,
                to: retainedFulfilmentFilters?.filters?.deliveredOn?.to?.$d,
              },
              pincodeIds: retainedFulfilmentFilters?.filters?.pincodes,
              statusTiming: retainedFulfilmentFilters?.filters?.statusTiming,
              libraryIds: retainedFulfilmentFilters?.filters?.name,
              city: retainedFulfilmentFilters?.filters?.city,
            },
          },
        }).catch(() => ({})),
      ]);

      dispatch({
        type: 'SET_ORDER_COUNT',
        data: {
          newOrderCount: placedResponse?.data?.newOrdersAdminV2?.count ?? 0,
          placedOrderCount:
            placedResponse?.data?.newOrdersAdminV2?.placedOrderCount ?? 0,
          confirmedOrderCount:
            confirmedResponse?.data?.newOrdersAdminV2?.count ?? 0,
          remainingConfirmedOrderCount:
            confirmedResponse?.data?.newOrdersAdminV2?.confirmedOrderCount ?? 0,
          returnOrderCount: returnResponse?.data?.returnOrdersAdmin?.count ?? 0,
          returnRequestedOrderCount:
            returnResponse?.data?.returnOrdersAdmin
              ?.returnRequestedOrderCount ?? 0,
          fulfilmentPossibilityOrderCount:
            fulfilmentResponse?.data?.fulfilmentPossibilityOrdersAdmin?.count ?? 0,
        },
      });
    } catch (error) {
      return error;
    }
  };

  useEffect(() => {
    getCountData();
  }, []);

  return (
    <Switch>
      <Route
        exact
        path={`${ROUTES?.ORDERS}${ROUTES?.NEW}`}
        render={() => (
          <RedirectControl
            redirectArray={redirectArray}
            route={`${ROUTES?.ORDERS}${ROUTES?.NEW}`}
          />
        )}
      />
      <Route
        exact
        path={`${ROUTES?.ORDERS}${ROUTES?.NEW}${ROUTES?.CONFIRM_ORDER}`}
        render={(props) => (
          <AccessControl permissionKey={PERMISSIONS_KEY?.ORDERS_NEW_PLACED}>
            <NewConfirmOrder {...props} />
          </AccessControl>
        )}
      />
      <Route
        exact
        path={`${ROUTES?.ORDERS}${ROUTES?.NEW}${ROUTES?.FULFILMENT_POSSIBILITY}`}
        render={(props) => {
          const key = permissions?.[PERMISSIONS_KEY?.ORDERS_NEW_FULFILMENT_POSSIBILITY]?.length > 0
            ? PERMISSIONS_KEY?.ORDERS_NEW_FULFILMENT_POSSIBILITY
            : PERMISSIONS_KEY?.ORDERS_NEW_PLACED;
          return (
            <AccessControl permissionKey={key}>
              <NewFulfilmentPossibility {...props} />
            </AccessControl>
          );
        }}
      />
      <Route
        exact
        path={`${ROUTES?.ORDERS}${ROUTES?.NEW}${ROUTES?.ASSIGN_DELIVERY_PARTNER}`}
        render={(props) => (
          <AccessControl permissionKey={PERMISSIONS_KEY?.ORDERS_NEW_CONFIRMED}>
            <NewAssignDelPartner {...props} />
          </AccessControl>
        )}
      />
      <Route
        exact
        path={`${ROUTES?.ORDERS}${ROUTES?.NEW}${ROUTES?.DELIVERY}`}
        render={(props) => (
          <AccessControl permissionKey={PERMISSIONS_KEY?.ORDERS_NEW_DELIVERY}>
            <NewDelivery {...props} />
          </AccessControl>
        )}
      />
      <Route
        exact
        path={`${ROUTES?.ORDERS}${ROUTES?.NEW}${ROUTES?.RETURN}`}
        render={(props) => (
          <AccessControl permissionKey={PERMISSIONS_KEY?.ORDERS_NEW_RETURN}>
            <NewReturn {...props} />
          </AccessControl>
        )}
      />
      <Route path="*" exact component={Error404} />
    </Switch>
  );
};

export default NewOrdersWrapper;
